import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface QuizEntryCounterProps {
  currentIndex: number;
  total: number;
  children: ReactNode;
  className?: string;
  /** Visible label before n/m. Defaults to "Frage"; review uses "Aufgabe" for reveals. */
  label?: string;
}

const MIN_ENTRIES_FOR_COUNTER = 2;
const FIRST_ENTRY_DISPLAY_NUMBER = 1;

/**
 * Prompt with "Frage n/m" immediately after the last word — used by
 * multi-entry inline quizzes, Selbstreflexion, and quiz review. A flex row
 * would pin the counter to the trailing edge; the prompt stays in the inline
 * flow instead. Same-line gap comes from the prompt's end margin so a wrapped
 * counter starts on the prompt's left edge.
 */
const QuizEntryCounter = ({
  currentIndex,
  total,
  children,
  className,
  label = "Frage",
}: QuizEntryCounterProps) => {
  const showCounter = total >= MIN_ENTRIES_FOR_COUNTER;
  const displayEntryNumber = currentIndex + FIRST_ENTRY_DISPLAY_NUMBER;

  return (
    <div className={cn("quiz-entry-counter", className)}>
      {children}
      {showCounter && (
        <span className="inline whitespace-nowrap font-semibold text-primary/70">
          {label} {displayEntryNumber}/{total}
        </span>
      )}
    </div>
  );
};

export default QuizEntryCounter;
